import React, { useState, useCallback, useEffect } from 'react';
import { getHomeTopGainers, getHomeTopLoosers, getHomeTopVolumes, getHomeFallingGainers, getHomeRisingLoosers, placeHomeOrder } from '../services/api';
import './HomePage.css';

const TABS = [
  { key: 'gainers',        label: 'Top Gainers' },
  { key: 'loosers',        label: 'Top Loosers' },
  { key: 'volumes',        label: 'Top Volumes' },
  { key: 'fallingGainers', label: 'Falling Gainers' },
  { key: 'risingLoosers',  label: 'Rising Loosers' },
];

const COLUMNS_BY_TAB = {
  gainers: [
    { key: 'symbol',             label: 'Symbol' },
    { key: 'price',               label: 'Price' },
    { key: 'changeFromPrevClosePct', label: 'Change from Prev Close' },
    { key: 'volume',              label: 'Volume' },
    { key: 'buyQty',               label: 'Buy Bids' },
    { key: 'sellQty',              label: 'Sell Bids' },
  ],
  loosers: [
    { key: 'symbol',             label: 'Symbol' },
    { key: 'price',               label: 'Price' },
    { key: 'changeFromPrevClosePct', label: 'Change from Prev Close' },
    { key: 'volume',              label: 'Volume' },
    { key: 'buyQty',               label: 'Buy Bids' },
    { key: 'sellQty',              label: 'Sell Bids' },
  ],
  volumes: [
    { key: 'symbol',             label: 'Symbol' },
    { key: 'price',               label: 'Price' },
    { key: 'changeFromPrevClosePct', label: 'Change from Prev Close' },
    { key: 'changeFromHighPct',   label: 'Change from Day High' },
    { key: 'changeFromLowPct',    label: 'Change from Day Low' },
    { key: 'volume',              label: 'Volume' },
    { key: 'buyQty',               label: 'Buy Bids' },
    { key: 'sellQty',              label: 'Sell Bids' },
  ],
};
COLUMNS_BY_TAB.fallingGainers = COLUMNS_BY_TAB.volumes;
COLUMNS_BY_TAB.risingLoosers  = COLUMNS_BY_TAB.volumes;

const DEFAULT_SORT = {
  gainers: { key: 'changeFromPrevClosePct', dir: 'desc' },
  loosers: { key: 'changeFromPrevClosePct', dir: 'asc' },
  volumes: { key: 'volume', dir: 'desc' },
  fallingGainers: { key: 'volume', dir: 'desc' },
  risingLoosers: { key: 'volume', dir: 'desc' },
};

const FETCH_BY_TAB = {
  gainers: getHomeTopGainers,
  loosers: getHomeTopLoosers,
  volumes: getHomeTopVolumes,
  fallingGainers: getHomeFallingGainers,
  risingLoosers: getHomeRisingLoosers,
};

function sortRows(rows, sortKey, sortDir) {
  return [...rows].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    return sortDir === 'asc' ? av - bv : bv - av;
  });
}

function ChangeCell({ value }) {
  if (value == null) return <td className="hp-num">—</td>;
  const cls = value > 0 ? 'hp-pos' : value < 0 ? 'hp-neg' : '';
  return <td className={`hp-num ${cls}`}>{value > 0 ? '+' : ''}{value.toFixed(2)}%</td>;
}

function OrderPopup({ row, amount, product, onClose, onSubmitted }) {
  const [orderType, setOrderType] = useState('MARKET');
  const [submitting, setSubmitting] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (transactionType) => {
    setError('');
    setSubmitting(transactionType);
    try {
      const result = await placeHomeOrder({
        symbol: row.symbol,
        transactionType,
        orderType,
        amount,
        product,
      });
      onSubmitted(`${transactionType} ${result.quantity}×${row.symbol} ${result.outcome === 'PLACED' ? 'placed' : result.outcome.toLowerCase()}`);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Order failed');
    } finally {
      setSubmitting('');
    }
  };

  return (
    <div className="hp-modal-backdrop" onClick={onClose}>
      <div className="hp-modal" onClick={e => e.stopPropagation()}>
        <div className="hp-modal-title">Place Order — {row.symbol}</div>
        <div className="hp-modal-row">
          <label>Order Type</label>
          <div className="hp-radio-group">
            <button className={`hp-radio-btn ${orderType === 'MARKET' ? 'hp-radio-active' : ''}`} onClick={() => setOrderType('MARKET')}>Market</button>
            <button className={`hp-radio-btn ${orderType === 'LIMIT' ? 'hp-radio-active' : ''}`} onClick={() => setOrderType('LIMIT')}>Limit</button>
          </div>
        </div>
        <div className="hp-modal-row hp-modal-info">
          Amount to invest: {amount} · Product: {product}
        </div>
        {error && <div className="hp-modal-error">{error}</div>}
        <div className="hp-modal-actions">
          <button className="hp-modal-cancel" onClick={onClose} disabled={!!submitting}>Cancel</button>
          <button className="hp-btn-buy" onClick={() => handleSubmit('BUY')} disabled={!!submitting}>
            {submitting === 'BUY' ? 'Placing…' : 'Buy'}
          </button>
          <button className="hp-btn-sell" onClick={() => handleSubmit('SELL')} disabled={!!submitting}>
            {submitting === 'SELL' ? 'Placing…' : 'Sell'}
          </button>
        </div>
      </div>
    </div>
  );
}

function HomeGrid({ tabKey, amount, product }) {
  const columns = COLUMNS_BY_TAB[tabKey];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [sortKey, setSortKey] = useState(DEFAULT_SORT[tabKey].key);
  const [sortDir, setSortDir] = useState(DEFAULT_SORT[tabKey].dir);
  const [popupRow, setPopupRow] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await FETCH_BY_TAB[tabKey]();
      const { key, dir } = DEFAULT_SORT[tabKey];
      setSortKey(key);
      setSortDir(dir);
      setRows(sortRows(data.rows || [], key, dir));
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [tabKey]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 5000);
    return () => clearTimeout(t);
  }, [message]);

  const handleSort = (key) => {
    let dir = 'desc';
    if (sortKey === key) dir = sortDir === 'desc' ? 'asc' : 'desc';
    setSortKey(key);
    setSortDir(dir);
    setRows(prev => sortRows(prev, key, dir));
  };

  return (
    <div className="hp-grid">
      <div className="hp-grid-header">
        {message && <div className="hp-banner hp-banner-ok">{message}</div>}
        {error && <div className="hp-banner hp-banner-err">{error}</div>}
        <button className="hp-btn-refresh" onClick={fetchRows} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <div className="hp-table-wrap">
        <table className="hp-table">
          <thead>
            <tr>
              {columns.map(col => (
                <th key={col.key} onClick={() => handleSort(col.key)} className={`hp-sortable ${col.key === 'symbol' ? 'hp-symbol-header' : ''}`}>
                  {col.label}
                  {sortKey === col.key && <span className="hp-sort-arrow">{sortDir === 'asc' ? ' ▲' : ' ▼'}</span>}
                </th>
              ))}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading ? (
              <tr><td colSpan={columns.length + 1} className="hp-no-data">No data</td></tr>
            ) : rows.map(row => (
              <tr key={row.symbol}>
                {columns.map(col => (
                  col.key === 'symbol' ? (
                    <td key={col.key} className="hp-symbol">{row.symbol}</td>
                  ) : col.key === 'price' || col.key === 'volume' || col.key === 'buyQty' || col.key === 'sellQty' ? (
                    <td key={col.key} className="hp-num">{col.key === 'price' ? row[col.key].toFixed(2) : row[col.key]}</td>
                  ) : (
                    <ChangeCell key={col.key} value={row[col.key]} />
                  )
                ))}
                <td className="hp-actions">
                  <button className="hp-btn-order" onClick={() => setPopupRow(row)}>Order</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {popupRow && (
        <OrderPopup
          row={popupRow}
          amount={amount}
          product={product}
          onClose={() => setPopupRow(null)}
          onSubmitted={setMessage}
        />
      )}
    </div>
  );
}

function HomePage() {
  const [amount, setAmount] = useState(() => localStorage.getItem('jt-home-amount') || '50000');
  const [product, setProduct] = useState(() => localStorage.getItem('jt-home-product') || 'MIS');
  const [activeTab, setActiveTab] = useState('gainers');

  useEffect(() => { localStorage.setItem('jt-home-amount', amount); }, [amount]);
  useEffect(() => { localStorage.setItem('jt-home-product', product); }, [product]);

  return (
    <div className="home-page">
      <div className="hp-config">
        <div className="hp-config-field">
          <label>Amount to invest</label>
          <input type="number" min="1" step="1" value={amount} onChange={e => setAmount(e.target.value)} />
        </div>
        <div className="hp-config-field">
          <label>Product Type</label>
          <div className="hp-radio-group">
            <button className={`hp-radio-btn ${product === 'MIS' ? 'hp-radio-active' : ''}`} onClick={() => setProduct('MIS')}>MIS (Intraday)</button>
            <button className={`hp-radio-btn ${product === 'CNC' ? 'hp-radio-active' : ''}`} onClick={() => setProduct('CNC')}>CNC (Delivery)</button>
          </div>
        </div>
      </div>

      <div className="hp-body">
        <div className="hp-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              className={`hp-tab-btn ${activeTab === t.key ? 'hp-tab-active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="hp-content">
          <HomeGrid key={activeTab} tabKey={activeTab} amount={amount} product={product} />
        </div>
      </div>
    </div>
  );
}

export default HomePage;
